import React, { useContext, useState } from 'react';
import { Linking, ScrollView, View } from 'react-native';
import {
  Button, Card, Dialog, Divider, IconButton, List, Portal, Switch, Text, TextInput,
} from 'react-native-paper';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { AuthContext } from '../../../context/AuthContext';
import { ROLE_NAMES, SCREEN_NAMES } from '../../../utils/constant';
import {
  NAS_FILES_FOR_ADMIN, NAS_MEDIA_FOR_USER, MY_NAS_MEDIA, NAS_MEDIA_DOWNLOAD_URL,
} from '../../../gql/nas/query';
import {
  ATTACH_NAS_MEDIA_TO_USER, DETACH_NAS_MEDIA_FROM_USER, SET_USER_NAS_BROWSE_ACCESS,
} from '../../../gql/nas/mutation';

const MIME_ICONS = {
  'image/': 'image',
  'video/': 'video',
  'audio/': 'music',
  'application/pdf': 'file-pdf-box',
};

function mimeIcon(mime) {
  if (!mime) return 'file';
  for (const [prefix, icon] of Object.entries(MIME_ICONS)) {
    if (mime.startsWith(prefix)) return icon;
  }
  return 'file';
}

function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

// Mini NAS browser used when admin picks a file to attach
function NasFilePicker({ onSelect, onCancel }) {
  const [currentPath, setCurrentPath] = useState(null);
  const [pathHistory, setPathHistory] = useState([]);
  const toast = useToast();

  const { data, loading } = useQuery(NAS_FILES_FOR_ADMIN, {
    variables: { path: currentPath },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });
  const items = data?.nasFilesForAdmin ?? [];

  const navigateTo = (path) => {
    setPathHistory((prev) => [...prev, currentPath]);
    setCurrentPath(path);
  };

  const navigateBack = () => {
    const prev = [...pathHistory];
    const last = prev.pop();
    setPathHistory(prev);
    setCurrentPath(last);
  };

  return (
    <View style={{ maxHeight: 400 }}>
      {pathHistory.length > 0 && (
        <Button icon="arrow-left" onPress={navigateBack} compact>
          Retour
        </Button>
      )}
      {loading && <Text style={{ padding: 8, opacity: 0.5 }}>Chargement...</Text>}
      <ScrollView>
        {items.map((item) => (
          <List.Item
            key={item.path}
            title={item.name}
            description={item.type === 'file' ? formatSize(item.size) : 'Dossier'}
            left={(props) => (
              <List.Icon {...props} icon={item.type === 'directory' ? 'folder' : mimeIcon(item.mime)} />
            )}
            onPress={() => {
              if (item.type === 'directory') navigateTo(item.path);
              else onSelect(item);
            }}
          />
        ))}
        {!loading && items.length === 0 && (
          <Text style={{ padding: 16, opacity: 0.5, textAlign: 'center' }}>Dossier vide</Text>
        )}
      </ScrollView>
      <Button onPress={onCancel}>Annuler</Button>
    </View>
  );
}

function NasMediaTab({ user }) {
  const { authState } = useContext(AuthContext);
  const navigation = useNavigation();
  const isAdmin = authState.role === ROLE_NAMES.SUPERADMIN;
  const isOwnProfile = authState.email === user.email;
  const toast = useToast();

  const [pickerVisible, setPickerVisible] = useState(false);
  const [confirmDetachId, setConfirmDetachId] = useState(null);

  const { data: adminData, loading: adminLoading, refetch: refetchAdmin } = useQuery(NAS_MEDIA_FOR_USER, {
    variables: { userId: user.id },
    skip: !isAdmin,
  });
  const { data: myData, loading: myLoading, refetch: refetchMine } = useQuery(MY_NAS_MEDIA, {
    skip: isAdmin || !isOwnProfile,
  });
  const loading = isAdmin ? adminLoading : myLoading;
  const mediaList = isAdmin ? (adminData?.nasMediaForUser ?? []) : (myData?.myNasMedia ?? []);
  const refetch = isAdmin ? refetchAdmin : refetchMine;

  const [getDownloadUrl] = useLazyQuery(NAS_MEDIA_DOWNLOAD_URL, {
    fetchPolicy: 'network-only',
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [attachMedia, { loading: attaching }] = useMutation(ATTACH_NAS_MEDIA_TO_USER, {
    onCompleted: () => { toast.show('Média attaché'); setPickerVisible(false); refetch(); },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [detachMedia, { loading: detaching }] = useMutation(DETACH_NAS_MEDIA_FROM_USER, {
    onCompleted: () => { toast.show('Média retiré'); setConfirmDetachId(null); refetch(); },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [setBrowseAccess, { loading: updatingBrowseAccess }] = useMutation(SET_USER_NAS_BROWSE_ACCESS, {
    onCompleted: (d) => {
      toast.show(d.setUserNasBrowseAccess.canBrowseNas
        ? 'Accès à la navigation NAS accordé'
        : 'Accès à la navigation NAS retiré');
    },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const handleOpen = async (mediaId) => {
    const result = await getDownloadUrl({ variables: { nasMediaId: mediaId } });
    const url = result?.data?.nasMediaDownloadUrl;
    if (url) Linking.openURL(url);
  };

  const handleSelectFile = (item) => {
    attachMedia({ variables: { userId: user.id, nasPath: item.path, displayName: item.name } });
  };

  const [browseAccess, setBrowseAccessValue] = useState(user.canBrowseNas ?? false);

  const handleToggleBrowseAccess = () => {
    const next = !browseAccess;
    setBrowseAccessValue(next);
    setBrowseAccess({ variables: { userId: user.id, canBrowse: next } });
  };

  if (loading) {
    return <Text style={{ padding: 16, textAlign: 'center', opacity: 0.5 }}>Chargement...</Text>;
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 12 }}>
      {isAdmin && (
        <Card style={{ marginBottom: 12 }}>
          <List.Item
            title="Navigation NAS"
            description="Autorise cet utilisateur à parcourir librement les dossiers du NAS (pas seulement les médias attachés)."
            descriptionNumberOfLines={3}
            right={() => (
              <Switch
                value={browseAccess}
                onValueChange={handleToggleBrowseAccess}
                disabled={updatingBrowseAccess}
              />
            )}
          />
        </Card>
      )}

      {isAdmin && (
        <Button
          mode="contained-tonal"
          icon="plus"
          onPress={() => setPickerVisible(true)}
          style={{ marginBottom: 12 }}
          loading={attaching}
        >
          Attacher un fichier NAS
        </Button>
      )}

      {isOwnProfile && !isAdmin && user.canBrowseNas && (
        <Button
          mode="contained-tonal"
          icon="folder-search"
          onPress={() => navigation.navigate(SCREEN_NAMES.NAS_BROWSE)}
          style={{ marginBottom: 12 }}
        >
          Parcourir le NAS
        </Button>
      )}

      {mediaList.length === 0 && (
        <Text style={{ textAlign: 'center', opacity: 0.5, marginTop: 32 }}>
          {isAdmin ? 'Aucun média NAS attaché à cet utilisateur' : 'Aucun média partagé pour le moment'}
        </Text>
      )}

      {mediaList.map((media) => (
        <Card key={media.id} style={{ marginBottom: 8 }}>
          <List.Item
            title={media.displayName}
            description={[formatSize(media.size), media.mime].filter(Boolean).join(' · ')}
            left={(props) => <List.Icon {...props} icon={mimeIcon(media.mime)} />}
            right={(props) => (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <IconButton
                  {...props}
                  icon="open-in-new"
                  onPress={() => handleOpen(media.id)}
                />
                {isAdmin && (
                  <IconButton
                    {...props}
                    icon="delete"
                    iconColor="red"
                    onPress={() => setConfirmDetachId(media.id)}
                  />
                )}
              </View>
            )}
          />
          {isAdmin && (
            <Text variant="bodySmall" style={{ paddingHorizontal: 16, paddingBottom: 8, opacity: 0.5 }}>
              Attaché par {media.attachedByName} le {new Date(media.attachedAt).toLocaleDateString('fr-FR')}
            </Text>
          )}
        </Card>
      ))}

      {/* File picker dialog */}
      <Portal>
        <Dialog visible={pickerVisible} onDismiss={() => setPickerVisible(false)}>
          <Dialog.Title>Choisir un fichier NAS</Dialog.Title>
          <Dialog.Content>
            <NasFilePicker
              onSelect={handleSelectFile}
              onCancel={() => setPickerVisible(false)}
            />
          </Dialog.Content>
        </Dialog>

        {/* Confirm detach */}
        <Dialog visible={!!confirmDetachId} onDismiss={() => setConfirmDetachId(null)}>
          <Dialog.Title>Retirer ce média ?</Dialog.Title>
          <Dialog.Content>
            <Text>Le fichier restera sur le NAS, seul le lien avec l'utilisateur sera supprimé.</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setConfirmDetachId(null)}>Annuler</Button>
            <Button
              onPress={() => detachMedia({ variables: { nasMediaId: confirmDetachId } })}
              loading={detaching}
              textColor="red"
            >
              Retirer
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </ScrollView>
  );
}

export default NasMediaTab;
